import { Link } from 'react-router-dom'
import { Button, Card, SkeletonStack } from '@/shared/ui'
import { useProfile, usePromptPreview } from '@/entities/profile'
import { toast } from '@/shared/model/toast'
import type { ProfileResponse } from '@/shared/api/types'
import styles from './ui/PromptPage.module.css'

/**
 * Готовый промпт — то, что уходит модели на каждом звонке.
 *
 * Показывать его целиком стоит по той причине, что иначе «агент говорит
 * не то» превращается в гадание: человек правит анкету наугад и не
 * видит, что изменилось. Здесь видно.
 */
export function PromptPage() {
  const preview = usePromptPreview()
  const profile = useProfile()

  async function copy() {
    if (!preview.data) return
    try {
      await navigator.clipboard.writeText(preview.data.prompt)
      toast.success('Промпт скопирован')
    } catch {
      // Буфер доступен не всегда: без https и без разрешения браузер
      // откажет. Молчать нельзя — человек нажал и ждёт.
      toast.error('Браузер не дал доступ к буферу обмена')
    }
  }

  return (
    <>
      <Parts profile={profile.data} length={preview.data?.prompt.length ?? 0} />

      <Card title="Первая фраза" subtitle="Звучит сразу, не дожидаясь модели">
        {preview.isPending ? (
          <SkeletonStack rows={1} height={48} />
        ) : (
          <p className={styles.greeting}>{preview.data?.greeting ?? '—'}</p>
        )}
      </Card>

      <Card
        title="Системный промпт"
        subtitle="Ваши данные — внутри разделов, общие правила речи идут после них"
        action={
          <Button variant="secondary" size="sm" onClick={copy} disabled={!preview.data}>
            Скопировать
          </Button>
        }
      >
        {preview.isPending ? (
          <SkeletonStack rows={8} height={16} />
        ) : (
          <>
            <pre className={styles.prompt}>{preview.data?.prompt ?? '—'}</pre>
            <div className={styles.meta}>
              <span>{preview.data?.prompt.length ?? 0} символов</span>
              <span>≈ {Math.round((preview.data?.prompt.length ?? 0) / 4)} токенов</span>
              <span>{preview.data?.prompt.split('\n').length ?? 0} строк</span>
            </div>
          </>
        )}
      </Card>
    </>
  )
}

/**
 * Из чего собран промпт.
 *
 * Каждый раздел — поле анкеты. Пустой кружок означает, что этого агент
 * не знает: на звонке он будет обходить тему или придумывать. Видеть
 * это надо здесь, а не по записи разговора.
 */
function Parts({ profile, length }: { profile?: ProfileResponse; length: number }) {
  const parts = [
    { name: 'Имя агента', value: profile?.agentName },
    { name: 'Название компании', value: profile?.companyLegal },
    { name: 'О компании', value: profile?.business },
    { name: 'Что предлагаем', value: profile?.offerings },
    { name: 'Цены и условия', value: profile?.pricing },
    { name: 'Чего обещать нельзя', value: profile?.forbidden },
    { name: 'Цель звонка', value: profile?.goal },
    { name: 'Дополнительно', value: profile?.extraPrompt },
  ]

  const filled = parts.filter((part) => part.value?.trim()).length

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>Из чего собран</div>

      {parts.map((part) => {
        const size = part.value?.trim().length ?? 0
        return (
          <div key={part.name} className={styles.part}>
            <span className={`${styles.dot} ${size > 0 ? styles.dotFilled : ''}`} />
            <span className={styles.partName}>{part.name}</span>
            <span className={styles.partSize}>{size > 0 ? size : '—'}</span>
          </div>
        )
      })}

      <div className={styles.note}>
        {filled === parts.length
          ? `Заполнено всё. Промпт — ${length} символов вместе с общими правилами речи.`
          : `Заполнено ${filled} из ${parts.length}. Незаполненное агент обойдёт или придумает на ходу.`}
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Link className={styles.link} to="/settings/profile">
            Править анкету
          </Link>
        </div>
      </div>
    </div>
  )
}
